// Thèmes de rendu des cartes ASCII. Un thème = une texture de tileset + une règle char -> tuile.
//  - 'greybox' : blocs de couleur générés au runtime (placeholder, par défaut).
//  - 'tinyTown' : vraies tuiles Kenney Tiny Town (16×16), avec auto-tiling des bâtiments.
import 'phaser'
import {
    GREYBOX_TEXTURE_KEY,
    charToIndex,
    solidIndices,
    ensureGreyboxTexture,
} from './greyboxTileset'
import { KENNEY, loadKenneySheet } from '../../assets/assetLibrary'

export type ThemeName = 'greybox' | 'tinyTown' | 'indoors'

export interface ResolvedTile {
    index: number
    solid: boolean
    /** frame à dessiner PAR-DESSUS (meuble/arbre sur fond transparent posé sur le sol/l'herbe) */
    overlay?: number
}

export interface MapTheme {
    /** clé de texture du tileset */
    textureKey: string
    /** espacement entre tuiles dans la feuille (Tiny Town packed = 0) */
    spacing: number
    /** preload() de la scène : charge la feuille si besoin (no-op pour greybox) */
    preload(scene: Phaser.Scene): void
    /** createMap() : génère la texture si besoin (greybox) */
    ensure(scene: Phaser.Scene): void
    /** index de tuile + collision pour la cellule (x,y) de la grille de caractères */
    resolve(grid: string[][], x: number, y: number): ResolvedTile
}

// --- Thème greybox (placeholder) ---
const gbIndex = charToIndex()
const gbSolids = solidIndices()
const greyboxTheme: MapTheme = {
    textureKey: GREYBOX_TEXTURE_KEY,
    spacing: 0,
    preload() { /* texture générée en create, pas de chargement */ },
    ensure(scene) { ensureGreyboxTexture(scene) },
    resolve(grid, x, y) {
        const ch = grid[y][x]
        const index = ch in gbIndex ? gbIndex[ch] : gbIndex['.']
        return { index, solid: gbSolids.has(index) }
    },
}

// --- Thème Tiny Town (Kenney) ---
// Index de tuiles repérés dans tilemap_packed.png (12 colonnes).
const TT = {
    grass: 0, grassAlt: 1, grassFlowers: 2,
    tree: 4, bush: 5,
    cour: 108,   // pierre grise -> dallage de la cour de récréation
    court: 42,   // sol clair -> terrain de sport
    // toit gris institutionnel
    roofTopL: 48, roofTopM: 49, roofTopR: 50,
    roofBodyL: 60, roofBodyM: 61, roofBodyR: 62,
    // mur (bas du bâtiment) + porte
    wallL: 76, wallM: 77, wallR: 79, door: 88,
}
const TINY_TOWN_KEY = 'tinytown'

// Une cellule fait-elle partie d'un bâtiment ? ('#' mur OU 'D' porte = bâtiment)
function isBuilding(grid: string[][], x: number, y: number): boolean {
    const row = grid[y]
    if (!row || x < 0 || x >= row.length) return false
    const c = row[x]
    return c === '#' || c === 'D'
}

// Variante d'herbe déterministe (un peu de vie sans bruit aléatoire).
function grassVariant(x: number, y: number): number {
    const h = ((x * 73856093) ^ (y * 19349663)) >>> 0
    const m = h % 100
    if (m < 3) return TT.grassFlowers
    if (m < 14) return TT.grassAlt
    return TT.grass
}

const tinyTownTheme: MapTheme = {
    textureKey: TINY_TOWN_KEY,
    spacing: 0,
    preload(scene) { loadKenneySheet(scene, TINY_TOWN_KEY, KENNEY.tilesets.tinyTown) },
    ensure() { /* chargé en preload */ },
    resolve(grid, x, y) {
        const ch = grid[y][x]
        switch (ch) {
            case '.': return { index: grassVariant(x, y), solid: false }
            case ',': return { index: TT.cour, solid: false }
            case 'C': return { index: TT.court, solid: false }
            // arbres/buissons (fond transparent) -> overlay sur l'herbe
            case 'T': return { index: grassVariant(x, y), solid: true, overlay: TT.tree }
            case 't': return { index: grassVariant(x, y), solid: true, overlay: TT.bush }
            case 'H': return { index: grassVariant(x, y), solid: true, overlay: TT.bush } // haie
            case 'D': return { index: TT.door, solid: false }  // porte (marchable, gérée par Door)
            case '#': {
                const down = isBuilding(grid, x, y + 1)
                const up = isBuilding(grid, x, y - 1)
                const left = isBuilding(grid, x - 1, y)
                const right = isBuilding(grid, x + 1, y)
                const pick = (l: number, m: number, r: number) => (!left ? l : !right ? r : m)
                let index: number
                if (!down) index = pick(TT.wallL, TT.wallM, TT.wallR)        // bas = mur
                else if (!up) index = pick(TT.roofTopL, TT.roofTopM, TT.roofTopR) // haut = faîte
                else index = pick(TT.roofBodyL, TT.roofBodyM, TT.roofBodyR)  // corps = toit
                return { index, solid: true }
            }
            default: return { index: grassVariant(x, y), solid: false }
        }
    },
}

// --- Thème intérieur (Kenney roguelikeSheet) : sol + mur + porte (+ meubles riches) ---
// Index repérés dans roguelikeSheet_transparent.png (57 colonnes, espacement 1) via _tileviewer2.html.
const RL = {
    floor: 7,    // dalle grise lisse (sol d'intérieur propre)
    wall: 700,   // mur clair
    door: 12,    // passage/porte (arche)
}
const RL_KEY = 'rlsheet'

// char -> MEUBLE posé sur le sol (overlay transparent, bloque la case).
const RL_FURNITURE: Record<string, number> = {
    B: 352,  // bureau (desk)
    K: 362,  // table
    c: 247,  // chaise
    S: 311,  // étagère (armoire à rayonnages)
    b: 70,   // lit
    p: 296,  // plante en pot (sur meuble bas)
    O: 138,  // bibliothèque (haute, livres)
    A: 313,  // armoire (haute)
    L: 370,  // casier / armoire métallique
    d: 310,  // commode (tiroirs)
    a: 191,  // fauteuil
    o: 126,  // canapé
    F: 30,   // frigo / gros électroménager (plan blanc)
    V: 453,  // four / fourneau (cuisine)
    E: 425,  // évier / lavabo
    U: 85,   // comptoir / plan de service (self)
    R: 619,  // comptoir caisse / banque d'accueil
    g: 80,   // guéridon / petite table
    m: 139,  // sac (farine / provisions)
    q: 727,  // plante sur pied (déco)
}
// char -> DÉCO MURALE (overlay posé sur un mur).
const RL_WALL_DECOR: Record<string, number> = {
    P: 479,  // poster / cadre
    W: 157,  // fenêtre
    N: 220,  // banderole / fanion
    h: 482,  // horloge
}

const indoorsTheme: MapTheme = {
    textureKey: RL_KEY,
    spacing: 1,
    preload(scene) { loadKenneySheet(scene, RL_KEY, KENNEY.tilesets.rpg) },
    ensure() { /* chargé en preload */ },
    resolve(grid, x, y) {
        const ch = grid[y][x]
        if (ch === '#') return { index: RL.wall, solid: true }
        if (ch === 'D') return { index: RL.door, solid: false }
        if (ch === '_') return { index: RL.floor, solid: false }
        // meuble : sol gris en base + meuble en overlay (bloque la case)
        if (ch in RL_FURNITURE) return { index: RL.floor, solid: true, overlay: RL_FURNITURE[ch] }
        // déco murale : mur en base + déco en overlay (placée à la place d'un '#')
        if (ch in RL_WALL_DECOR) return { index: RL.wall, solid: true, overlay: RL_WALL_DECOR[ch] }
        return { index: RL.floor, solid: false }
    },
}

const THEMES: Record<ThemeName, MapTheme> = {
    greybox: greyboxTheme,
    tinyTown: tinyTownTheme,
    indoors: indoorsTheme,
}

export function getTheme(name: ThemeName = 'greybox'): MapTheme {
    return THEMES[name] ?? greyboxTheme
}
